import type { ClarifyingQuestion, QuestionAnswer } from '@revelio/core';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import { dashMetaFetch } from '../../shared/api/dash-meta-fetch';
import type { LLMTabContentForApi } from '../CustomDash/components/AIAssistant/convert-tab-content-to-llm';
import {
  applyLlmTabContentToTabDispatch,
  type LLMTabContentForDispatch,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { TabContentAction } from '../CustomDash/components/PageConfigProvider/page-reducer';
import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import type { AiSearchApplySource } from '../CustomDash/filters/ai-search/useApplyAiSearchFilters';
import type {
  CustomDashAssistantEntityType,
  GlobalAssistantConversationTurn,
} from './global-ai-assistant-api';
import { reportLoopError } from './global-ai-loop-error-telemetry';
import { parsePageStreamEnvelope } from './global-ai-page-stream-event.schema';

export type GlobalAssistantConversationPayload = {
  turns: GlobalAssistantConversationTurn[];
  summary?: string;
};

const normalizePreviousPrompts = (
  previousPrompts: string[] | undefined
): string[] =>
  (previousPrompts ?? [])
    .map((prompt) => prompt.trim())
    .filter(Boolean)
    .slice(-3);

export type PageDecisionDigest = {
  title: string;
  action: 'dropped' | 'swapped' | 'filter_added';
  from?: string;
  to?: string;
  reason: string;
};

export type DashboardPageStreamEvent =
  | { type: 'thinking'; content: string }
  | { type: 'status'; content: string }
  | { type: 'intent'; content: unknown }
  | { type: 'config'; content: LLMTabContentForDispatch }
  | { type: 'filters'; content: TalentDiscoveryAiFilterSearchResponseV2 }
  | { type: 'error'; content: string }
  | { type: 'verifying'; content: string }
  | { type: 'finding'; content: PageDecisionDigest }
  | {
      type: 'questions';
      content: { questions: ClarifyingQuestion[]; triggerReason: string };
    };

export interface DashboardPageStreamResult {
  /**
   * The `PositionedItem`s from the LAST applied `config` event — i.e. the
   * items currently mounted in the tab (each dispatch replaces the previous).
   * These carry the exact ids the widgets mount with, so the narration
   * collector must be armed with these ids rather than a re-conversion of the
   * raw config (which would mint fresh, non-matching uuids).
   */
  dispatchedItems: PositionedItem[];
}

export async function consumeSse(
  response: Response,
  onLine: (line: string) => void,
  signal: AbortSignal | undefined
): Promise<void> {
  if (!response.body) {
    throw new Error('No response body');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal?.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        // Flush any pending multibyte bytes + a complete final line left in the
        // buffer. A stream that ends WITHOUT a trailing newline (e.g. a server
        // crash / proxy cutoff mid-event) would otherwise silently drop its
        // last line — which the turn loop reads as a clean, successful end
        // rather than the truncation it is.
        buffer += decoder.decode();
        if (buffer.length > 0) {
          onLine(buffer);
          buffer = '';
        }
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        onLine(line);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

type DashboardPageStreamApplyFilters = (
  response: TalentDiscoveryAiFilterSearchResponseV2,
  options?: {
    primarySelectionMergeMode?: 'merge' | 'replace';
    skipTalentDiscoveryAdaptFallback?: boolean;
    source?: AiSearchApplySource;
    pairRoleTaxonomyWithResponse?: boolean;
  }
) => Promise<unknown>;

/**
 * The body fields the create and update endpoints spell identically. Each
 * spread is conditional so an absent option stays absent from the payload
 * (the endpoints treat `undefined` and missing differently for validation).
 */
const buildCommonStreamBodyFields = (params: {
  model?: string;
  answers?: QuestionAnswer[];
  rolesTax?: string;
  skillsTax?: number;
  activitiesTax?: number;
  activitiesEnabled?: boolean;
  geoTax?: number;
  conversation?: GlobalAssistantConversationPayload;
}): Record<string, unknown> => {
  const {
    model,
    answers,
    rolesTax,
    skillsTax,
    activitiesTax,
    activitiesEnabled,
    geoTax,
    conversation,
  } = params;
  return {
    ...(model && { model }),
    ...(answers && answers.length > 0 && { answers }),
    ...(rolesTax && { rolesTax }),
    ...(skillsTax !== undefined && skillsTax !== 0 && { skillsTax }),
    // 0 is the server-side default, so omitting it keeps the payload minimal
    // with identical behaviour (same treatment as `skillsTax` above).
    ...(activitiesTax !== undefined &&
      activitiesTax !== 0 && { activitiesTax }),
    ...(activitiesEnabled === true && { activitiesEnabled: true }),
    ...(geoTax !== undefined && { geoTax }),
    ...(conversation && (conversation.turns.length > 0 || conversation.summary)
      ? { conversation }
      : {}),
  };
};

/**
 * Shared create/update core: POST the SSE endpoint, consume the stream through
 * the page-stream envelope, dispatch each `config` event into the tab, then
 * apply the last `filters` event. The create/update wrappers differ ONLY in
 * endpoint + body.
 *
 * Envelope: an unparsable line or a schema-failing known event is dropped AND
 * reported (`page_stream_envelope` telemetry, mirroring the turn-step stream)
 * — without that report a malformed `config` event is indistinguishable from a
 * page that simply didn't build. Valid events flow through; unknown event
 * types that parse are forwarded and ignored by consumers.
 */
async function runDashboardPageStream(params: {
  endpointPath: string;
  body: Record<string, unknown>;
  entityType: CustomDashAssistantEntityType;
  dispatch: (action: TabContentAction) => void;
  applyAiSearchFilters: DashboardPageStreamApplyFilters;
  onEvent?: (event: DashboardPageStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<DashboardPageStreamResult> {
  const {
    endpointPath,
    body,
    entityType,
    dispatch,
    applyAiSearchFilters,
    onEvent,
    signal,
  } = params;

  const response = await dashMetaFetch(endpointPath, {
    method: 'POST',
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}`);
  }

  const primaryFilterId = PRIMARY_FILTER_BY_AI_ENTITY[entityType];

  const pendingFiltersRef: {
    current: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  } = { current: undefined };
  // Track the items from the LAST applied config event so the bridge can arm
  // the narration collector with the exact ids the widgets mounted with.
  let dispatchedItems: PositionedItem[] = [];
  // Collected, not dispatched, while the stream is open — committed below with
  // the filters, once the build is known to have finished uncancelled.
  const pendingConfigRef: {
    current:
      | Extract<DashboardPageStreamEvent, { type: 'config' }>['content']
      | undefined;
  } = { current: undefined };

  await consumeSse(
    response,
    (line) => {
      if (!line.startsWith('data: ')) {
        return;
      }
      const jsonStr = line.slice(6);
      if (!jsonStr.trim()) {
        return;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(jsonStr);
      } catch {
        void reportLoopError({
          source: 'page_stream_envelope',
          errorClass: 'json',
        });
        return;
      }
      const result = parsePageStreamEnvelope(parsed);
      if (!result.ok) {
        void reportLoopError({
          source: 'page_stream_envelope',
          errorClass: 'schema',
        });
        return;
      }
      const event = result.event as DashboardPageStreamEvent;
      try {
        onEvent?.(event);
        if (event.type === 'config') {
          pendingConfigRef.current = event.content;
        }
        if (event.type === 'filters') {
          pendingFiltersRef.current = event.content;
        }
      } catch {
        // A throwing onEvent handler must not kill the stream.
      }
    },
    signal
  );

  // Nothing is committed to the page until the stream has ended UNCANCELLED.
  // `consumeSse` loops on `while (!signal?.aborted)`, so a mid-stream abort
  // ends it GRACEFULLY rather than throwing, and the yield below is a second
  // window a Stop click lands in. Without this check a build the user
  // CANCELLED still landed whatever it had streamed so far — its layout under
  // the page's old filters, or its filters under the old layout. Deferring
  // both to here costs nothing visible: the backend emits `config` and
  // `filters` back to back at the end of the stream (the last `config` wins,
  // as the dispatched-items tracking always did). Returning rather than
  // throwing keeps a deliberate Stop from surfacing as "Generation failed".
  await new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, 0);
  });
  if (signal?.aborted) {
    return { dispatchedItems: [] };
  }

  const pendingConfig = pendingConfigRef.current;
  if (pendingConfig !== undefined) {
    try {
      const applied = applyLlmTabContentToTabDispatch(
        dispatch,
        pendingConfig,
        primaryFilterId
      );
      dispatchedItems = applied.items;
    } catch {
      // A throwing dispatch must not surface as a failed generation — the same
      // swallow the in-loop dispatch had; the page simply does not build.
    }
  }

  const pendingFilters = pendingFiltersRef.current;
  if (pendingFilters === undefined) {
    return { dispatchedItems };
  }

  await applyAiSearchFilters(pendingFilters, {
    primarySelectionMergeMode: 'replace',
    skipTalentDiscoveryAdaptFallback: true,
    source: 'global_assistant',
    // Generated pages carry AI-resolved role/skill ids exactly like a route
    // apply does, so they need the same taxonomy pairing — without it the
    // generated page labels and queries those ids under whatever taxonomy the
    // scope holds ("engineers" landing as "Customer Operations"). The role
    // side is still gated on a response-supplied role inside the hook.
    pairRoleTaxonomyWithResponse: true,
  });

  return { dispatchedItems };
}

export async function runDashboardPageStreamCreate(params: {
  prompt: string;
  entityType: CustomDashAssistantEntityType;
  model?: string;
  answers?: QuestionAnswer[];
  rolesTax?: string;
  skillsTax?: number;
  /**
   * NOTE: there is no `activitiesTaxAdaptive` twin here on purpose. The
   * page-stream DTOs carry no adaptive field for ANY taxonomy — not role, not
   * skill — and the controller reads its fields explicitly, so an adaptive flag
   * sent on this path is silently dropped. Sending one anyway would look wired
   * and do nothing. The turn-step and prefetch paths DO honour adaptive.
   */
  activitiesTax?: number;
  /**
   * The account has the activity filter. Sent only when
   * definitively true — never `false` while Unleash is loading, so a
   * still-loading flag is never read server-side as a confirmed capability.
   */
  activitiesEnabled?: boolean;
  /**
   * Geography taxonomy id (metro 1 / administrative 2). When omitted, the
   * backend resolves geography under metro.
   */
  geoTax?: number;
  conversation?: GlobalAssistantConversationPayload;
  /**
   * Forwarded to the page-stream backend. When false the build best-guesses
   * instead of asking clarifying questions — set by committed builds so the
   * generation never bails to a question after the user chose to build.
   */
  allowClarification?: boolean;
  dispatch: (action: TabContentAction) => void;
  applyAiSearchFilters: DashboardPageStreamApplyFilters;
  onEvent?: (event: DashboardPageStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<DashboardPageStreamResult> {
  const {
    prompt,
    entityType,
    allowClarification,
    dispatch,
    applyAiSearchFilters,
    onEvent,
    signal,
  } = params;
  return runDashboardPageStream({
    endpointPath: '/api/build-a-dash/ai-gen/page-stream',
    body: {
      prompt,
      entityType,
      ...buildCommonStreamBodyFields(params),
      ...(allowClarification !== undefined && { allowClarification }),
    },
    entityType,
    dispatch,
    applyAiSearchFilters,
    onEvent,
    signal,
  });
}

/** Streamed update of the current tab from natural language (Custom Dash). */
export async function runDashboardPageStreamUpdate(params: {
  newPrompt: string;
  entityType: CustomDashAssistantEntityType;
  currentConfig: LLMTabContentForApi;
  currentFilterContext?: string;
  previousPrompts?: string[];
  model?: string;
  answers?: QuestionAnswer[];
  rolesTax?: string;
  skillsTax?: number;
  activitiesTax?: number;
  /**
   * The account has the activity filter. Sent only when
   * definitively true — never `false` while Unleash is loading, so a
   * still-loading flag is never read server-side as a confirmed capability.
   */
  activitiesEnabled?: boolean;
  /**
   * Geography taxonomy id (metro 1 / administrative 2). When omitted, the
   * backend resolves geography under metro.
   */
  geoTax?: number;
  conversation?: GlobalAssistantConversationPayload;
  dispatch: (action: TabContentAction) => void;
  applyAiSearchFilters: DashboardPageStreamApplyFilters;
  onEvent?: (event: DashboardPageStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<DashboardPageStreamResult> {
  const {
    newPrompt,
    entityType,
    currentConfig,
    currentFilterContext,
    previousPrompts,
    dispatch,
    applyAiSearchFilters,
    onEvent,
    signal,
  } = params;
  const normalizedPreviousPrompts = normalizePreviousPrompts(previousPrompts);
  return runDashboardPageStream({
    endpointPath: '/api/build-a-dash/ai-gen/page-update-from-config-stream',
    body: {
      newPrompt,
      currentConfig,
      ...(currentFilterContext && { currentFilterContext }),
      ...(normalizedPreviousPrompts.length > 0
        ? { previousPrompts: normalizedPreviousPrompts }
        : {}),
      entityType,
      ...buildCommonStreamBodyFields(params),
    },
    entityType,
    dispatch,
    applyAiSearchFilters,
    onEvent,
    signal,
  });
}
